// src/components/LineRow.jsx
import React, { useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Stack,
  IconButton,
  Divider,
  Typography,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { useData } from "../hooks/useData";
import { usePricing } from "../hooks/usePricing";
import ColorSelect from "./ColorSelect";
import FontSelect from "./FontSelect";
import SizeSelect from "./SizeSelect";
import { getColorsByProductCode } from "../constants/colors";
import { setDimensions } from "../utils/dimensions";
import { FONTS } from "../constants/fonts";
import { IText } from "fabric";

export default function LineRow({ lineId }) {
  const {
    lines,
    updateLine,
    removeLine,
    canvas,
    setCanvasObj,
    selectedProduct,
    addLine,
  } = useData();

  const { updatePricing } = usePricing();

  const line = lines.find((l) => l.id === lineId);
  const lineIndex = lines.findIndex((l) => l.id === lineId);
  const isLastLine = lineIndex === lines.length - 1;
  const canAddMore = lines.length < 5;

  // Inside LineRow.jsx — REPLACE THE WHOLE useEffect
  useEffect(() => {
    if (!canvas || !selectedProduct?.code || !line) return;

    const colors = getColorsByProductCode(selectedProduct.code);
    const colorObj = colors.find((c) => c.code === line.color);
    const fillColor = colorObj?.hex || "#000000";

    let obj = line.canvasObj;

    // ALWAYS CREATE OR UPDATE TEXT OBJECT
    if (!obj) {
      obj = new IText(line.text || " ", {
        left: 0,
        top: 100 + lineIndex * 80,
        fontFamily: line.font,
        fontSize: parseInt(line.size) * 25,
        fill: fillColor,
        selectable: true,
        hasControls: true,
        originX: "left",
        originY: "center",
        textAlign: "center",
        opacity: line.text?.trim() ? 1 : 0.3,
      });

      canvas.add(obj);
      setCanvasObj(lineId, obj);
    } else {
      obj.set({
        text: line.text || " ",
        fontFamily: line.font,
        fontSize: parseInt(line.size) * 25,
        fill: fillColor,
        opacity: line.text?.trim() ? 1 : 0.3,
      });
    }

    // FORCE RE-CENTER WITH CORRECT WIDTH — THIS FIXES TEXT DISAPPEARANCE
    canvas.requestRenderAll();
    obj.setCoords();

    const updatePosition = () => {
      const canvasWidth = canvas.getWidth();
      const textWidth = obj.getScaledWidth();
      const maxWidth = canvasWidth * 0.95; // respect padding

      obj.set({
        left: (canvasWidth - Math.min(textWidth, maxWidth)) / 2,
        originX: "left",
      });
      obj.setCoords();
      canvas.requestRenderAll();
    };

    // Run multiple times to ensure correct bounds
    setTimeout(updatePosition, 0);
    setTimeout(updatePosition, 50);
    setTimeout(updatePosition, 100);

    updatePricing();
  }, [
    canvas,
    line,
    line?.text,
    line?.font,
    line?.color,
    line?.size,
    lineId,
    lineIndex,
    selectedProduct?.code,
    setCanvasObj,
    updatePricing,
  ]);

  const dimensions = useMemo(() => {
    if (!line?.canvasObj?.text) return null;
    const fabricObj = line.canvasObj;
    if (!fabricObj.text || fabricObj.text.trim() === "") return null;

    return setDimensions(fabricObj, line.font, line.size, FONTS, {
      productCode: selectedProduct?.code,
      heightOverride: selectedProduct?.sizes.find((s) => s.code === line.size)
        ?.height,
      scaleX: fabricObj.scaleX || 1,
      precision: "precise",
    });
  }, [line?.canvasObj, line?.font, line?.size, selectedProduct]);

  if (!line) return null;

  const linePrice = Number(line.price || 0).toFixed(2);
  const charCount = line.text?.trim().length || 0;

  const handleText = (e) => {
    const newText = e.target.value;
    updateLine(lineId, { text: newText });
    setTimeout(() => updatePricing(), 0);
  };

  const handleDelete = () => {
    if (line.canvasObj) canvas.remove(line.canvasObj);
    removeLine(lineId);
    updatePricing();
  };

  const handleAddLine = () => addLine();

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid #eee",
        mb: 1.5,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* ROW 1: Controls (responsive column on mobile) */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 1 }}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <SizeSelect lineId={lineId} />
        <FontSelect lineId={lineId} />
        <ColorSelect lineId={lineId} />

        <TextField
          size="small"
          label="Text"
          value={line.text || ""}
          onChange={handleText}
          placeholder="Enter text..."
          sx={{
            flex: "1 1 200px",
            minWidth: 0,
          }}
        />
      </Stack>
      <Divider sx={{ my: 1 }} />
      {/* ROW 2: Info + Actions (always separate row, full width on mobile) */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={0} // ← ZERO
        sx={{
          mt: { xs: -1.5, sm: 0 }, // ← PULL UP ON MOBILE
          mb: { xs: 0.5, sm: 0 },
          gap: 0,
        }}
      >
        {/* Left */}
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{ fontSize: "0.875rem" }}
        >
          {line.sizeName} • {line.colorName} • {line.font}
        </Typography>

        {/* Right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          {charCount > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "success.main",
                fontWeight: "bold",
                fontSize: "0.875rem",
              }}
            >
              ${linePrice}
            </Typography>
          )}
          <IconButton size="small" onClick={handleDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
          {isLastLine && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleAddLine}
              disabled={!canAddMore}
            >
              {canAddMore ? "Add Line" : "Max 5"}
            </Button>
          )}
        </Box>
      </Stack>
      {/* Mobile-only Dimensions (below buttons) */}
      {dimensions && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: { xs: "block", sm: "none" },
            mt: 1,
            fontStyle: "italic",
            textAlign: "left",
          }}
        >
          ({dimensions.widthInches.toFixed(2)}" ×{" "}
          {dimensions.heightInches.toFixed(2)}")
        </Typography>
      )}
    </Box>
  );
}
